/**
 * Graph 탭 — ServiceDetailPage 내부에서 렌더. 하이브리드 로딩(overview + ego merge) 관리.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { AdminClient, OntologyBlock } from "../../api/admin-client.ts"
import { KIND_COLORS } from "./graph-colors.ts"
import { LAYER_COLORS, LAYER_LABELS, LAYERS, type Layer } from "./graph-layers.ts"
import { KnowledgeGraph, type KnowledgeGraphHandle } from "./KnowledgeGraph.tsx"
import { NodeDetailPanel } from "./NodeDetailPanel.tsx"
import type { GraphEdge, GraphNode } from "./types.ts"

interface GraphTabProps {
	readonly slug: string
	readonly client: AdminClient
	/**
	 * 초기 진입 시 overview 대신 이 nodeId 를 seed 로 ego 로드.
	 * GraphTabWithPlayback 의 서버 검색 결과 클릭 경로에서 사용된다.
	 */
	readonly initialSeedId?: string
	readonly initialSeedName?: string
	readonly initialSeedKind?: string
	readonly initialSeedDomain?: string
}

const DEFAULT_OVERVIEW_LIMIT = 500
const DEFAULT_EGO_LIMIT = 500
const LIMIT_OPTIONS = [500, 1000, 2000] as const

export function GraphTab({
	slug,
	client,
	initialSeedId,
	initialSeedName,
	initialSeedKind,
	initialSeedDomain
}: GraphTabProps) {
	const [nodes, setNodes] = useState<GraphNode[]>([])
	const [edges, setEdges] = useState<GraphEdge[]>([])
	const [truncated, setTruncated] = useState(false)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | undefined>()
	const [selected, setSelected] = useState<GraphNode | null>(null)
	const [search, setSearch] = useState("")
	const [blocks, setBlocks] = useState<ReadonlyArray<OntologyBlock>>([])
	const [overviewLimit, setOverviewLimit] = useState<number>(DEFAULT_OVERVIEW_LIMIT)
	/** 엣지 클릭 힌트 — localStorage 로 영구 숨김 가능 */
	const [edgeHintDismissed, setEdgeHintDismissed] = useState<boolean>(() => {
		try {
			return localStorage.getItem("knowledge-edge-hint-dismissed") === "1"
		} catch {
			return false
		}
	})
	const [totalNodes, setTotalNodes] = useState<number | null>(null)
	const [totalEdges, setTotalEdges] = useState<number | null>(null)
	const graphRef = useRef<KnowledgeGraphHandle>(null)

	// 블럭 카탈로그 prefetch — NodeDetailPanel 이 kind 역참조로 example 노출에 사용.
	useEffect(() => {
		let cancelled = false
		void (async () => {
			try {
				const list = await client.getOntologyBlocks()
				if (!cancelled) setBlocks(list)
			} catch {
				// 블럭 로드 실패해도 그래프 자체는 동작 — 조용히 무시
			}
		})()
		return () => {
			cancelled = true
		}
	}, [client])

	// 전체 노드/엣지 수 prefetch — 렌더된 샘플 vs 전체 규모 비교 표기용.
	useEffect(() => {
		let cancelled = false
		void (async () => {
			try {
				const usage = await client.getOntologyUsage(slug)
				if (cancelled) return
				const nodeSum = Object.values(usage.kinds).reduce((a, b) => a + b, 0)
				const edgeSum = Object.values(usage.relations).reduce((a, b) => a + b, 0)
				setTotalNodes(nodeSum)
				setTotalEdges(edgeSum)
			} catch {
				// 실패해도 그래프 자체는 동작 — 총계만 표기 안 됨
			}
		})()
		return () => {
			cancelled = true
		}
	}, [client, slug])

	const loadOverview = useCallback(async () => {
		setLoading(true)
		setError(undefined)
		try {
			const res = await client.getSampleGraph(slug, {
				mode: "overview",
				limit: overviewLimit
			})
			setNodes([...res.nodes])
			setEdges([...res.edges])
			setTruncated(res.truncated)
		} catch (e) {
			setError((e as Error).message)
		} finally {
			setLoading(false)
		}
	}, [client, slug, overviewLimit])

	// biome-ignore lint/correctness/useExhaustiveDependencies: initialSeedName/Kind/Domain 는 초기 렌더 스냅샷 — 이후 갱신 시 재로드하지 않도록 의도적 제외
	useEffect(() => {
		// initialSeedId 가 있으면 overview 를 스킵하고 바로 ego 로드한다.
		// GraphTabWithPlayback 의 서버 검색 결과 클릭 경로에서 Neptune OOM 을 피하면서
		// 사용자가 고른 노드를 즉시 보여주기 위함.
		if (initialSeedId) {
			setLoading(false)
			void expandEgo({
				id: initialSeedId,
				name: initialSeedName ?? initialSeedId,
				kind: initialSeedKind ?? "",
				domain: initialSeedDomain ?? ""
			} as GraphNode)
			return
		}
		void loadOverview()
	}, [loadOverview, initialSeedId])

	// ego 확장 — 클릭된 seed 에서 1홉 확장 후 기존 그래프에 merge
	const expandEgo = useCallback(
		async (seed: GraphNode) => {
			try {
				const res = await client.getSampleGraph(slug, {
					mode: "ego",
					seeds: [seed.id],
					hops: 1,
					limit: DEFAULT_EGO_LIMIT
				})
				setNodes((prev) => {
					const seen = new Set(prev.map((n) => n.id))
					const merged = [...prev]
					for (const n of res.nodes) {
						if (!seen.has(n.id)) {
							merged.push({ ...n })
							seen.add(n.id)
						}
					}
					return merged
				})
				setEdges((prev) => {
					const seen = new Set(prev.map((e) => e.id ?? ""))
					const merged = [...prev]
					for (const e of res.edges) {
						const key = e.id ?? `${e.source}|${e.target}|${e.relation}`
						if (!seen.has(key)) {
							merged.push({ ...e, id: key })
							seen.add(key)
						}
					}
					return merged
				})
			} catch (e) {
				setError((e as Error).message)
			}
		},
		[client, slug]
	)

	/** blocks → relation displayName 맵. fallback: raw name */
	const relationDisplayNames = useMemo(() => {
		const m = new Map<string, string>()
		for (const b of blocks) {
			for (const r of b.slice.relations) {
				if (r.displayName) m.set(r.name, r.displayName)
			}
		}
		return m
	}, [blocks])

	/** blocks → kind displayName 맵. fallback: raw kind */
	const kindDisplayNames = useMemo(() => {
		const m = new Map<string, string>()
		for (const b of blocks) {
			for (const k of b.slice.kinds) {
				if (k.displayName) m.set(k.name, k.displayName)
			}
		}
		return m
	}, [blocks])

	/** blocks → relation directionality 맵 */
	const relationDirectionality = useMemo(() => {
		const m = new Map<string, "directed" | "undirected">()
		for (const b of blocks) {
			for (const r of b.slice.relations) {
				if (r.directionality) m.set(r.name, r.directionality)
			}
		}
		return m
	}, [blocks])

	const kindOptions = useMemo(() => {
		const set = new Set<string>()
		for (const n of nodes) set.add(n.kind)
		return [...set].sort()
	}, [nodes])
	const [selectedKind, setSelectedKind] = useState<string | undefined>()
	// 레이어 토글 — 비어있으면 전부 표시, 하나라도 켜지면 해당 레이어만
	const [enabledLayers, setEnabledLayers] = useState<ReadonlySet<Layer>>(new Set())
	const toggleLayer = useCallback((layer: Layer) => {
		setEnabledLayers((prev) => {
			const next = new Set(prev)
			if (next.has(layer)) next.delete(layer)
			else next.add(layer)
			return next
		})
	}, [])

	// overview 로드가 실패해도 이름/키워드로 서버 검색 (knowledge_search 하이브리드) 해서
	// 매칭된 노드를 시드로 ego 탐색할 수 있게 한다.
	const [serverHits, setServerHits] = useState<
		ReadonlyArray<{ nodeId: string; domain: string; kind: string; name: string }>
	>([])
	const [searchLoading, setSearchLoading] = useState(false)
	const runServerSearch = useCallback(async () => {
		const q = search.trim()
		if (!q) {
			setServerHits([])
			return
		}
		setSearchLoading(true)
		try {
			const hits = await client.searchSeedNodes(slug, q, 20)
			setServerHits(hits)
		} catch (e) {
			setError((e as Error).message)
		} finally {
			setSearchLoading(false)
		}
	}, [client, slug, search])
	const selectHit = useCallback(
		(hit: { nodeId: string; domain: string; kind: string; name: string }) => {
			void expandEgo({
				id: hit.nodeId,
				label: hit.name,
				name: hit.name,
				kind: hit.kind,
				domain: hit.domain
			} as GraphNode)
			setServerHits([])
		},
		[expandEgo]
	)

	return (
		<div className="panel" style={{ padding: 0, overflow: "hidden" }}>
			{error && (
				<div className="alert alert-danger" style={{ margin: "10px 14px" }}>
					그래프 로드 실패: {error}
					<div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
						→ 검색창에 이름/키워드 입력 후 Enter 로 서버 검색 → 결과 클릭 → ego 로드.
					</div>
				</div>
			)}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 12,
					padding: "10px 14px",
					borderBottom: "1px solid var(--border)",
					flexWrap: "wrap"
				}}
			>
				<input
					type="text"
					placeholder="노드 검색 — 로드된 그래프 필터 + Enter 는 서버 하이브리드 검색"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault()
							void runServerSearch()
						}
					}}
					style={{
						flex: "1 1 240px",
						padding: "6px 10px",
						border: "1px solid var(--border)",
						borderRadius: 6,
						background: "var(--bg-elev)",
						color: "inherit"
					}}
				/>
				<button
					type="button"
					className="btn btn-ghost btn-sm"
					onClick={() => void runServerSearch()}
					disabled={searchLoading}
				>
					{searchLoading ? "검색 중…" : "서버 검색"}
				</button>
				<select
					value={selectedKind ?? ""}
					onChange={(e) => setSelectedKind(e.target.value || undefined)}
					style={{
						padding: "6px 10px",
						border: "1px solid var(--border)",
						borderRadius: 6,
						background: "var(--bg-elev)",
						color: "inherit"
					}}
				>
					<option value="">kind 전체</option>
					{kindOptions.map((k) => (
						<option key={k} value={k}>
							{k}
						</option>
					))}
				</select>
				<button type="button" className="btn btn-ghost btn-sm" onClick={() => graphRef.current?.fitView()}>
					맞춤 보기
				</button>
				<button
					type="button"
					className="btn btn-ghost btn-sm"
					onClick={() => {
						setSelected(null)
						void loadOverview()
					}}
				>
					리셋
				</button>
				<label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--muted)" }}>
					최대 노드 수
					<select
						value={overviewLimit}
						onChange={(e) => setOverviewLimit(Number(e.target.value))}
						style={{
							padding: "4px 8px",
							border: "1px solid var(--border)",
							borderRadius: 6,
							background: "var(--bg-elev)",
							color: "inherit"
						}}
					>
						{LIMIT_OPTIONS.map((n) => (
							<option key={n} value={n}>
								{n}
							</option>
						))}
					</select>
				</label>
				<span style={{ fontSize: 12, color: "var(--muted)" }}>
					노드 {nodes.length.toLocaleString()}
					{totalNodes !== null && ` / ${totalNodes.toLocaleString()}`} · 엣지 {edges.length.toLocaleString()}
					{totalEdges !== null && ` / ${totalEdges.toLocaleString()}`}
					{truncated && (
						<span style={{ color: "var(--danger, #f59e0b)", marginLeft: 4 }}>· 일부만 표시됨</span>
					)}
				</span>
			</div>

			{serverHits.length > 0 && (
				<div
					style={{
						borderBottom: "1px solid var(--border)",
						maxHeight: 240,
						overflowY: "auto",
						background: "var(--bg)"
					}}
				>
					<div
						style={{
							padding: "6px 14px",
							fontSize: 11,
							color: "var(--muted)",
							display: "flex",
							alignItems: "center",
							gap: 8
						}}
					>
						<span>서버 검색 결과 {serverHits.length} — 클릭 시 ego 로드</span>
						<button
							type="button"
							className="btn btn-ghost btn-sm"
							style={{ marginLeft: "auto" }}
							onClick={() => setServerHits([])}
						>
							닫기
						</button>
					</div>
					{serverHits.map((hit) => (
						<button
							key={hit.nodeId}
							type="button"
							onClick={() => selectHit(hit)}
							style={{
								display: "block",
								width: "100%",
								textAlign: "left",
								padding: "8px 14px",
								border: 0,
								borderTop: "1px solid var(--border)",
								background: "transparent",
								color: "inherit",
								cursor: "pointer",
								fontSize: 13
							}}
						>
							<div style={{ fontWeight: 500 }}>{hit.name}</div>
							<div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
								{hit.domain} · {hit.kind} · {hit.nodeId}
							</div>
						</button>
					))}
				</div>
			)}

			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 6,
					padding: "8px 14px",
					borderBottom: "1px solid var(--border)",
					flexWrap: "wrap",
					fontSize: 12
				}}
			>
				<span style={{ color: "var(--muted)", marginRight: 4 }}>레이어</span>
				{LAYERS.map((layer: Layer) => {
					const active = enabledLayers.has(layer)
					return (
						<button
							key={layer}
							type="button"
							onClick={() => toggleLayer(layer)}
							style={{
								padding: "3px 10px",
								borderRadius: 999,
								border: `1px solid ${active ? LAYER_COLORS[layer] : "var(--border)"}`,
								background: active ? LAYER_COLORS[layer] : "transparent",
								color: active ? "#fff" : "var(--muted)",
								fontSize: 12,
								cursor: "pointer"
							}}
						>
							{LAYER_LABELS[layer]}
						</button>
					)
				})}
				{enabledLayers.size > 0 && (
					<button
						type="button"
						onClick={() => setEnabledLayers(new Set())}
						style={{
							marginLeft: 4,
							padding: "3px 10px",
							borderRadius: 999,
							border: "1px solid var(--border)",
							background: "transparent",
							color: "var(--muted)",
							fontSize: 12,
							cursor: "pointer"
						}}
					>
						전체 보기
					</button>
				)}
			</div>

			<div style={{ display: "flex", minHeight: "calc(100vh - 320px)", position: "relative" }}>
				<div style={{ flex: 1, minWidth: 0, position: "relative" }}>
					{loading && nodes.length === 0 ? (
						<div
							style={{
								padding: 40,
								textAlign: "center",
								color: "var(--muted)"
							}}
						>
							불러오는 중…
						</div>
					) : (
						<>
							<KnowledgeGraph
								ref={graphRef}
								nodes={nodes}
								edges={edges}
								search={search}
								selectedKind={selectedKind}
								enabledLayers={enabledLayers}
								onNodeSelect={setSelected}
								onNodeDblClick={(n: GraphNode) => void expandEgo(n)}
								relationDisplayNames={relationDisplayNames}
								kindDisplayNames={kindDisplayNames}
								relationDirectionality={relationDirectionality}
							/>
							{/* 범례 오버레이 — 현재 표시 중인 unique kind 목록 */}
							<GraphLegend nodes={nodes} kindDisplayNames={kindDisplayNames} />
							{/* 엣지 클릭 힌트 — 첫 클릭 전까지만 표시 */}
							{!edgeHintDismissed && (
								<div
									style={{
										position: "absolute",
										bottom: 12,
										left: 12,
										zIndex: 4,
										background: "var(--pk-bg-elevated, var(--bg-elev))",
										border: "1px solid var(--border)",
										borderRadius: "var(--pk-radius-md, 8px)",
										padding: "6px 10px",
										fontSize: 12,
										color: "var(--muted)",
										display: "flex",
										alignItems: "center",
										gap: 6,
										pointerEvents: "auto"
									}}
								>
									<span>💡 엣지를 클릭하면 상세 정보가 열립니다</span>
									<button
										type="button"
										onClick={() => {
											setEdgeHintDismissed(true)
											try {
												localStorage.setItem("knowledge-edge-hint-dismissed", "1")
											} catch {
												// no-op
											}
										}}
										aria-label="힌트 닫기"
										style={{
											background: "transparent",
											border: "none",
											cursor: "pointer",
											color: "var(--muted)",
											fontSize: 12,
											padding: "0 2px",
											lineHeight: 1
										}}
									>
										✕
									</button>
								</div>
							)}
						</>
					)}
				</div>

				<div
					style={{
						width: 320,
						borderLeft: "1px solid var(--border)",
						background: "var(--bg-elev)",
						overflowY: "auto"
					}}
				>
					<NodeDetailPanel node={selected} onExpand={(n) => void expandEgo(n)} blocks={blocks} />
				</div>
			</div>
		</div>
	)
}

/**
 * 범례 오버레이 — 캔버스 우상단에 현재 표시 중인 kind 목록을 색 닷과 함께 표시.
 * 4개 초과 시 "+N 더보기" 접힘.
 */
function GraphLegend({
	nodes,
	kindDisplayNames
}: {
	readonly nodes: ReadonlyArray<GraphNode>
	readonly kindDisplayNames: ReadonlyMap<string, string>
}) {
	const [expanded, setExpanded] = useState(false)

	const uniqueKinds = useMemo(() => {
		const seen = new Set<string>()
		for (const n of nodes) seen.add(n.kind)
		return [...seen].sort()
	}, [nodes])

	if (uniqueKinds.length === 0) return null

	const MAX_VISIBLE = 4
	const visible = expanded ? uniqueKinds : uniqueKinds.slice(0, MAX_VISIBLE)
	const overflow = uniqueKinds.length - MAX_VISIBLE

	return (
		<div
			style={{
				position: "absolute",
				top: 12,
				right: 12,
				zIndex: 4,
				background: "var(--pk-bg-elevated, var(--bg-elev))",
				border: "1px solid var(--border)",
				borderRadius: "var(--pk-radius-md, 8px)",
				padding: "8px 10px",
				fontSize: 11,
				minWidth: 120,
				maxWidth: 200,
				pointerEvents: "auto"
			}}
		>
			{visible.map((kind) => {
				const color = KIND_COLORS[kind] ?? KIND_COLORS.default ?? "#6e7681"
				const label = kindDisplayNames.get(kind) ?? kind
				return (
					<div
						key={kind}
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							marginBottom: 4,
							overflow: "hidden"
						}}
					>
						<span
							style={{
								width: 8,
								height: 8,
								borderRadius: "50%",
								background: color,
								flexShrink: 0
							}}
							aria-hidden
						/>
						<span
							style={{
								color: "var(--muted)",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis"
							}}
							title={kind}
						>
							{label}
						</span>
					</div>
				)
			})}
			{!expanded && overflow > 0 && (
				<button
					type="button"
					onClick={() => setExpanded(true)}
					style={{
						background: "transparent",
						border: "none",
						cursor: "pointer",
						fontSize: 11,
						color: "var(--muted)",
						padding: 0,
						marginTop: 2
					}}
				>
					+{overflow} 더보기
				</button>
			)}
			{expanded && overflow > 0 && (
				<button
					type="button"
					onClick={() => setExpanded(false)}
					style={{
						background: "transparent",
						border: "none",
						cursor: "pointer",
						fontSize: 11,
						color: "var(--muted)",
						padding: 0,
						marginTop: 2
					}}
				>
					접기
				</button>
			)}
		</div>
	)
}
